import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  position?: "top" | "left" | "right" | "bottom";
  tooltipText: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

const Tooltip = ({
  position = "top",
  tooltipText,
  children,
  className,
  disabled,
}: TooltipProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isHovering && triggerRef.current && !disabled) {
      const rect = triggerRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (position) {
        case "top":
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
        case "bottom":
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
      }

      setTooltipPosition({ top, left });
    }
  }, [isHovering, position, disabled]);

  const getTooltipStyles = () => {
    switch (position) {
      case "top":
        return {
          tooltip: "translate(-50%, -100%)",
          arrow: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2",
        };
      case "bottom":
        return {
          tooltip: "translate(-50%, 0)",
          arrow: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2",
        };
      case "left":
        return {
          tooltip: "translate(-100%, -50%)",
          arrow: "right-0 top-1/2 -translate-y-1/2 translate-x-1/2",
        };
      case "right":
        return {
          tooltip: "translate(0, -50%)",
          arrow: "left-0 top-1/2 -translate-y-1/2 -translate-x-1/2",
        };
    }
  };

  const styles = getTooltipStyles();

  return (
    <div
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {children}

      {isMounted &&
        isHovering &&
        !disabled &&
        createPortal(
          <div
            className={`fixed z-50 flex items-center rounded-lg bg-white px-2 py-1 shadow-lg ${className}`}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: styles.tooltip,
            }}
          >
            <span className=" text-[10px] text-slate-700 2xl:text-xs">
              {tooltipText}
            </span>
            <div
              className={`absolute rotate-45 bg-inherit p-1 ${styles.arrow}`}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export { Tooltip };

